import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Loader2, MapPin, Pencil, Search, Check } from "lucide-react";

const STATES = ["QLD", "NSW", "VIC", "SA", "WA", "TAS", "NT", "ACT"];

const compose = (p) => [p.address_line_1, p.address_line_2, p.suburb, p.state, p.postcode]
  .map((v) => (v || "").trim()).filter(Boolean).join(", ");

// Address entry: map search first, manual entry as the fallback.
// `value` is the full address string. `parts` (optional) holds the manual fields
// so the caller can persist them; otherwise they're kept locally.
export function AddressField({ value, onChange, parts, onPartsChange, disabled,
                              testid = "address-field", placeholder = "Start typing an address…" }) {
  const [local, setLocal] = useState(parts || {});
  const p = parts || local;
  const setParts = (patch) => {
    const next = { ...p, ...patch };
    (onPartsChange || setLocal)(next);
    onChange(compose(next));
  };
  const [manual, setManual] = useState(!!p.manual_entry);
  const [q, setQ] = useState(value || "");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const typedRef = useRef(false);   // only what the user types triggers a search

  useEffect(() => { setQ(value || ""); }, [value]);

  useEffect(() => {
    if (manual || !typedRef.current) return;
    const term = (q || "").trim();
    if (term.length < 3) { setResults([]); return; }
    const t = setTimeout(async () => {
      setLoading(true);
      try { const d = await api.addressSearch(term); setResults(d.results || []); setOpen(true); }
      catch { setResults([]); }
      finally { setLoading(false); }
    }, 400);
    return () => clearTimeout(t);
  }, [q, manual]);

  useEffect(() => {
    const h = (e) => { if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const pick = (label) => {
    typedRef.current = false;
    setQ(label); setOpen(false); setResults([]);
    (onPartsChange || setLocal)({ ...p, manual_entry: 0 });
    onChange(label);
  };

  const goManual = () => {
    setManual(true); setOpen(false);
    // Seed the manual fields from whatever is already typed/stored so the office
    // edits the address rather than retyping it.
    const seed = { ...p, manual_entry: 1 };
    const hasParts = [p.address_line_1, p.suburb, p.postcode].some((v) => (v || "").trim());
    if (!hasParts && (value || "").trim()) {
      const bits = String(value).split(",").map((b) => b.trim()).filter(Boolean);
      seed.address_line_1 = bits[0] || value;
      if (bits.length > 1) {
        seed.suburb = seed.suburb || bits[1]
          .replace(/\b(QLD|NSW|VIC|SA|WA|TAS|NT|ACT)\b/i, "")
          .replace(/\b\d{4}\b/, "").trim();
      }
      const pc = String(value).match(/\b(\d{4})\b/);
      if (pc) seed.postcode = seed.postcode || pc[1];
      const st = String(value).match(/\b(QLD|NSW|VIC|SA|WA|TAS|NT|ACT)\b/i);
      if (st) seed.state = st[1].toUpperCase();
    }
    (onPartsChange || setLocal)(seed);
  };
  const goSearch = () => {
    setManual(false);
    (onPartsChange || setLocal)({ ...p, manual_entry: 0 });
  };

  if (manual) {
    return (
      <div className="space-y-2" data-testid={`${testid}-manual`}>
        <Input value={p.address_line_1 || ""} disabled={disabled} className="h-9 text-sm"
          onChange={(e) => setParts({ address_line_1: e.target.value })}
          placeholder="Street address" data-testid={`${testid}-line1`} />
        <Input value={p.address_line_2 || ""} disabled={disabled} className="h-8 text-xs"
          onChange={(e) => setParts({ address_line_2: e.target.value })}
          placeholder="Unit / building (optional)" data-testid={`${testid}-line2`} />
        <div className="grid sm:grid-cols-2 gap-2">
          <Input value={p.suburb || ""} disabled={disabled} className="h-8 text-xs"
            onChange={(e) => setParts({ suburb: e.target.value })}
            placeholder="Suburb" data-testid={`${testid}-suburb`} />
          <div className="flex gap-2">
            <Select value={p.state || "QLD"} onValueChange={(v) => setParts({ state: v })} disabled={disabled}>
              <SelectTrigger className="h-8 w-24 text-xs" data-testid={`${testid}-state`}><SelectValue /></SelectTrigger>
              <SelectContent>{STATES.map((o) => <SelectItem key={o} value={o} className="text-sm">{o}</SelectItem>)}</SelectContent>
            </Select>
            <Input value={p.postcode || ""} disabled={disabled} className="h-8 text-xs flex-1"
              onChange={(e) => setParts({ postcode: e.target.value })}
              placeholder="Postcode" data-testid={`${testid}-postcode`} />
          </div>
        </div>
        <div className="flex items-center justify-between">
          <span className="text-[10px] text-slate-400 truncate">{value || "—"}</span>
          {!disabled && (
            <button type="button" onClick={goSearch} data-testid={`${testid}-use-search`}
              className="text-[11px] font-medium text-[#bf3b4f] hover:underline flex items-center gap-1 shrink-0">
              <Search className="h-3 w-3" /> Search for an address instead
            </button>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-1.5" ref={boxRef}>
      <div className="relative">
        <Input data-testid={testid} value={q} disabled={disabled} autoComplete="off"
          onChange={(e) => { typedRef.current = true; setQ(e.target.value); onChange(e.target.value); }}
          onFocus={() => results.length && setOpen(true)}
          className="h-9 text-sm pr-7" placeholder={placeholder} />
        {loading
          ? <Loader2 className="h-3.5 w-3.5 animate-spin absolute right-2 top-1/2 -translate-y-1/2 text-slate-400" />
          : value ? <Check className="h-3.5 w-3.5 absolute right-2 top-1/2 -translate-y-1/2 text-emerald-500" /> : null}
        {open && results.length > 0 && (
          <div data-testid={`${testid}-list`} className="absolute z-50 mt-1 w-full max-h-56 overflow-auto rounded-md border border-slate-200 bg-white shadow-lg">
            {results.map((r, i) => (
              <button type="button" key={i} data-testid={`${testid}-opt-${i}`} onClick={() => pick(r.label)}
                className="flex w-full items-start gap-2 px-3 py-2 text-left text-[11px] hover:bg-slate-50 border-b border-slate-50 last:border-0">
                <MapPin className="h-3 w-3 mt-0.5 text-[#bf3b4f] shrink-0" />
                <span className="text-slate-700">{r.label}</span>
              </button>
            ))}
          </div>
        )}
      </div>
      {!disabled && (
        <button type="button" onClick={goManual} data-testid={`${testid}-manual-toggle`}
          className="text-[11px] font-medium text-slate-500 hover:text-[#bf3b4f] hover:underline flex items-center gap-1">
          <Pencil className="h-3 w-3" /> Can't find it? Enter manually
        </button>
      )}
    </div>
  );
}
